<template>
  <section
    ref="bannerEl"
    class="ca-banner"
    :class="[
      `ca-banner--${currentTheme}`,
      {
        'ca-banner--publicidade': !!currentSlideData.publicidade,
        'ca-banner--no-media': !currentMedia,
        'ca-banner--fixed': fixedLayerEnabled,
      },
    ]"
  >
    <div class="ca-banner__fixed">
      <BannerSlider
        :active-slide="activeSlide"
        :slide-count="slideCount"
        :autoplay-delay="autoplayDelay"
        :show-side-arrow="showSideArrow && hasMultipleSlides"
        @update:activeSlide="activeSlide = $event"
      >
      <template #default>
        <div
          class="ca-banner__strip"
          :class="{ 'no-transition': skipTransition }"
          :style="{ transform: `translateX(-${stripOffset * 100}%)` }"
        >
          <div
            v-for="(slide, idx) in stripSlides"
            :key="idx"
            class="ca-banner__slide"
            :aria-hidden="slide.isClone ? 'true' : undefined"
          >
            <div
              class="ca-banner__slide-content ca-bullet-container flex flex-col pb-size-12 max-md:pt-0 max-md:pb-[32px] max-md:justify-end"
            >
              <p
                v-show="slide.eyebrow"
                class="ca-banner__eyebrow paragraph-medium uppercase overflow-hidden"
                :class="
                  (slide.theme || 'dark') === 'dark'
                    ? 'text-text-headers'
                    : 'text-white'
                "
              >
                {{ slide.eyebrow }}
              </p>

              <component
                :is="slide.titleHeadingTag"
                class="heading-jumbo mt-size-3 md:mt-size-4"
                :class="
                  (slide.theme || 'dark') === 'dark'
                    ? 'text-text-headers'
                    : 'text-white'
                "
              >
                {{ slide.title }}
              </component>

              <component
                v-show="slide.description"
                :is="slide.descriptionHeadingTag"
                class="ca-banner__description caption-large overflow-hidden mt-size-4 md:mt-size-5"
                :class="
                  (slide.theme || 'dark') === 'dark'
                    ? 'text-text-headers'
                    : 'text-white'
                "
              >
                {{ slide.description }}
              </component>

              <div
                v-show="slide.primaryLabel || slide.secondaryLabel"
                class="ca-banner__actions flex flex-wrap mt-size-5 md:mt-size-6 md:gap-size-5 max-md:w-full"
              >
                <BaseButton
                  v-if="slide.primaryLabel"
                  :label="slide.primaryLabel"
                  :class="
                    (slide.theme || 'dark') !== 'dark'
                      ? 'inverted primary button'
                      : 'theme primary button'
                  "
                  @click="navigateTo(slide.primaryHref)"
                />

                <BaseButton
                  v-if="slide.secondaryLabel"
                  :label="slide.secondaryLabel"
                  :class="
                    (slide.theme || 'dark') !== 'dark'
                      ? 'inverted secondary button'
                      : 'theme secondary button'
                  "
                  @click="navigateTo(slide.secondaryHref)"
                />
              </div>

              <component
                v-show="slide.legalDescription"
                :is="slide.legalDescriptionHeadingTag"
                class="ca-banner__legal caption-small mt-size-5 md:mt-size-6"
                :class="
                  (slide.theme || 'dark') === 'dark'
                    ? 'text-[#646F76]'
                    : 'text-white'
                "
              >
                {{ slide.legalDescription }}
              </component>

              <div
                v-show="slide.badgeSrc"
                class="ca-banner__badge mt-size-5 md:mt-size-6 w-fit rounded-[8px] p-[16px]"
                :class="{
                  'ca-banner__badge--grey': slide.badgeBackgroundColor === 'grey',
                  'ca-banner__badge--green': slide.badgeBackgroundColor === 'green',
                }"
              >
                <img
                  class="min-h-[32px] max-h-[48px] w-auto block"
                  :src="slide?.badgeSrc"
                  :alt="slide.badgeAlt || ''"
                />
              </div>
            </div>

            <div v-show="slide.imageSrc || slide.mobileImageSrc || slide.tabletImageSrc" class="ca-banner__slide-image">
              <picture>
                <source
                  v-if="slide.mobileImageSrc"
                  media="(max-width: 767px)"
                  :srcset="slide.mobileImageSrc"
                />
                <source
                  v-if="slide.tabletImageSrc"
                  media="(min-width: 768px) and (max-width: 1024px)"
                  :srcset="slide.tabletImageSrc"
                />
                <img
                  class="ca-banner__image"
                  :src="slide.imageSrc"
                  :alt="slide.imageAlt || ''"
                  loading="lazy"
                />
              </picture>
            </div>

            <div
              class="ca-banner__slide-footer ca-bullet-container flex items-center"
            >
              <span
                v-if="slide.publicidade"
                class="ca-banner__publicidade caption-small whitespace-nowrap uppercase max-md:order-last"
                :class="
                  slide.publicidadeColor === 'black'
                    ? 'text-text-headers'
                    : 'text-white'
                "
                >{{ slide.publicidade }}</span
              >
              <div class="flex-1" />
              <IconButton
                class="ca-banner__scroll-btn max-md:hidden shadow-2"
                type="inverted"
                variant="clear"
                size="xl"
                aria-label="Ir para a secção seguinte"
                @clicked="scrollToNext"
              >
                <template #trailingIcon>
                  <MouseScrollIcon
                    class="ca-banner__scroll-icon w-size-5 h-size-5"
                    :class="
                      slide.publicidadeColor === 'black'
                        ? 'text-text-headers'
                        : 'text-white'
                    "
                    aria-hidden="true"
                  />
                </template>
              </IconButton>
            </div>
          </div>
        </div>
      </template>

      <template #footer="{ isPlaying, dotProgress, togglePlay, goToSlide }">
        <BannerFooter
          :slide-count="slideCount"
          :active-slide="activeSlide"
          :show-controls="showControls"
          :show-controls-mobile="showControlsMobile"
          :is-playing="isPlaying"
          :dot-progress="dotProgress"
          @toggle-play="togglePlay"
          @go-to-slide="goToSlide"
        />
      </template>
      </BannerSlider>
    </div>
  </section>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>
<script setup>
import { computed, ref, watch, onBeforeUnmount, onMounted } from "vue";
import BannerSlider from "./BannerSlider.vue";
import BannerFooter from "./BannerFooter.vue";
import { BaseButton, IconButton } from "ca-online-design-system";
import MouseScrollIcon from "../../../assets/mouse_scroll.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaBanner",
});

const props = defineProps({
  slides: {
    type: Array,
    default: null,
  },
  autoplayDelay: {
    type: Number,
    default: 7000,
  },
  showControls: {
    type: Boolean,
    default: true,
  },
  showControlsMobile: {
    type: Boolean,
    default: true,
  },
  showSideArrow: {
    type: Boolean,
    default: true,
  },
  showCounter: {
    type: Boolean,
    default: true,
  },
});

const slidesRef = ref(props.slides ?? null);
const slidesData = computed(() => slidesRef.value || []);

const activeSlide = ref(0);
const stripIndex = ref(1); // +1 because of prepended clone
const skipTransition = ref(false);
const slideCount = computed(() => slidesData.value.length || 1);
const hasMultipleSlides = computed(() => slidesData.value.length > 1);
const stripOffset = computed(() =>
  hasMultipleSlides.value ? stripIndex.value : 0,
);

// Slides with clones: [clone-last, ...real slides, clone-first]
const stripSlides = computed(() => {
  const s = slidesData.value.map((slide, index) => ({
    ...slide,
    titleHeadingTag: normalizeHeadingTag(
      slide.titleHeadingType,
      index === 0 ? "h1" : "h2",
    ),
    descriptionHeadingTag: normalizeHeadingTag(
      slide.descriptionHeadingType,
      "p",
    ),
    legalDescriptionHeadingTag: normalizeHeadingTag(
      slide.legalDescriptionHeadingType,
      "p",
    ),
  }));
  if (s.length < 2) return s;
  return [
    {
      ...s[s.length - 1],
      titleHeadingTag: "p",
      descriptionHeadingTag: "p",
      legalDescriptionHeadingTag: "p",
      isClone: true,
    },
    ...s,
    {
      ...s[0],
      titleHeadingTag: "p",
      descriptionHeadingTag: "p",
      legalDescriptionHeadingTag: "p",
      isClone: true,
    },
  ];
});

let wrapTimeout = null;

watch(activeSlide, (newVal, oldVal) => {
  if (wrapTimeout) {
    clearTimeout(wrapTimeout);
    wrapTimeout = null;
  }

  const count = slideCount.value;
  const isWrapForward = oldVal === count - 1 && newVal === 0;
  const isWrapBackward = oldVal === 0 && newVal === count - 1;

  if (isWrapForward) {
    stripIndex.value = count + 1;
    wrapTimeout = setTimeout(() => {
      skipTransition.value = true;
      stripIndex.value = 1;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          skipTransition.value = false;
          wrapTimeout = null;
        });
      });
    }, 1000);
  } else if (isWrapBackward) {
    stripIndex.value = 0;
    wrapTimeout = setTimeout(() => {
      skipTransition.value = true;
      stripIndex.value = count;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          skipTransition.value = false;
          wrapTimeout = null;
        });
      });
    }, 1000);
  } else {
    stripIndex.value = newVal + 1;
  }
});

const currentSlideData = computed(
  () => slidesData.value[activeSlide.value] || {},
);
const currentTheme = computed(() => currentSlideData.value.theme || "dark");
const currentMedia = computed(
  () =>
    currentSlideData.value.imageSrc ||
    currentSlideData.value.mobileImageSrc ||
    currentSlideData.value.tabletImageSrc ||
    "",
);
const publicidadeColorClass = computed(() =>
  currentSlideData.value.publicidadeColor === "black"
    ? "text-text-headers"
    : "text-white",
);
// All main content (eyebrow, title, description) shares the same theme colour
const contentColorClass = computed(() =>
  currentTheme.value === "dark" ? "text-text-headers" : "text-white",
);
const legalColorClass = computed(() =>
  currentTheme.value === "dark" ? "text-[#646F76]" : "text-white",
);

const bannerEl = ref(null);
const fixedLayerEnabled = ref(false);

function navigateTo(url) {
  if (url) window.location.href = url;
}

function scrollToNext() {
  if (!bannerEl.value) return;
  const nextSection = bannerEl.value.nextElementSibling;
  if (nextSection) {
    nextSection.scrollIntoView({ behavior: "smooth" });
  } else {
    window.scrollTo({
      top: bannerEl.value.offsetTop + bannerEl.value.offsetHeight,
      behavior: "smooth",
    });
  }
}

const loadBannerMocks = async () => {
  if (!isDev) return;
  if (slidesRef.value) return;

  const { slides } = await import("./mocks/bannerMocks.js");
  slidesRef.value = slides;
};

onMounted(() => {
  loadBannerMocks();

  const host = bannerEl.value?.closest(".ds-scope") ?? bannerEl.value;
  const pageShell = document.querySelector(".ca-page-shell");
  fixedLayerEnabled.value = Boolean(
    host &&
      pageShell &&
      host.compareDocumentPosition(pageShell) & Node.DOCUMENT_POSITION_FOLLOWING,
  );

  document.documentElement.classList.toggle(
    "ca-fixed-banner-page",
    fixedLayerEnabled.value,
  );
});

onBeforeUnmount(() => {
  document.documentElement.classList.remove("ca-fixed-banner-page");
});
</script>

<style scoped lang="scss">
.ca-banner {
  position: relative;
  width: 100%;
  height: 100svh;
  min-height: 700px;
}

.ca-banner__fixed {
  position: relative;
  width: 100%;
  height: 100svh;
  min-height: 700px;
  overflow: hidden;
  touch-action: pan-y;

  :deep(*:focus:not(:focus-visible)) {
    outline: none;
  }
}

.ca-banner--fixed .ca-banner__fixed {
  position: fixed;
  inset: 0;
  z-index: 0;
}

.ca-banner__strip {
  position: absolute;
  inset: 0;
  display: flex;
  transition: transform 1s ease-in-out;

  &.no-transition {
    transition: none;
  }
}

.ca-banner__slide {
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
}

.ca-banner__slide-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 120px;
}

.ca-banner__slide-image {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.ca-banner__slide-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding-bottom: 44px;
  margin-bottom: 30px;
}

.ca-banner__scroll-icon :deep(path) {
  stroke: currentColor !important;
}

.ca-banner__scroll-btn {
  background-color: var(--ca-color-neutrals-inverted-20) !important;
  border: none !important;

  &:hover {
    background-color: var(--ca-color-neutrals-inverted-30) !important;
  }

  &:active {
    background-color: var(--ca-color-neutrals-inverted-40, rgba(255, 255, 255, 0.4)) !important;
  }
}

.ca-banner__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.ca-banner__badge--grey {
  background-color: var(--ca-color-neutrals-inverted-30);
}

.ca-banner__badge--green {
  background-color: var(--ca-color-theme-primary-50);
}

.ca-banner__eyebrow {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
}

.ca-banner__description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.ca-banner__actions {
  gap: 12px;
}

.ca-banner__legal {
  font-weight: 500 !important;
}

@media (min-width: 1025px) {
  .ca-banner__slide {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  }

  .ca-banner__slide-content {
    grid-column: 2 / 8;
    grid-row: 1;
  }

  .ca-banner__slide-image {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }


  .ca-banner__legal {
    font-size: 16px !important;
    font-weight: 500 !important;
  }

  .ca-banner__description {
    font-size: 16px !important;
  }
}

@media (max-width: 1024px) {
  .ca-banner,
  .ca-banner__fixed {
    min-height: 860px;
  }

  .ca-banner__slide-content {
    justify-content: flex-end;
    padding-bottom: 180px;
  }

  .ca-banner--no-media :deep(.ca-banner__layout) {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .ca-banner--no-media :deep(.ca-banner__footer) {
    position: relative;
    width: 100%;
  }

  .ca-banner__eyebrow,
  .ca-banner__description {
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 22px !important;
    letter-spacing: 0.168px !important;
  }
}

@media (max-width: 767px) {
  .ca-banner,
  .ca-banner__fixed {
    min-height: 680px;
  }

  .ca-banner__slide-content {
    position: absolute;
    top: auto;
    right: 0;
    bottom: 120px;
    left: 0;
    height: auto;
    max-height: 340px;
    justify-content: flex-start;
    padding-top: 12px;
    padding-bottom: 12px;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent,
      #000 12px,
      #000 calc(100% - 12px),
      transparent
    );
    mask-image: linear-gradient(
      to bottom,
      transparent,
      #000 12px,
      #000 calc(100% - 12px),
      transparent
    );
  }

  .ca-banner__slide-content > * {
    flex-shrink: 0;
  }

  .ca-banner__slide-footer {
    z-index: 6;
    margin-bottom: 16px;
  }

  .ca-banner__description {
    -webkit-line-clamp: 4;
    line-clamp: 4;
  }
}

@media (min-width: 1920px) {
  .ca-banner__slide {
    grid-template-columns: 1fr repeat(12, calc((1920px - 640px) / 12)) 1fr;
  }

  .ca-banner__slide-content {
    grid-column: 3 / 9;
    padding-left: 0;
    padding-right: 0;
  }

  .ca-banner__slide-image {
    position: absolute;
    inset: 0;
  }
}
</style>
